import dayjs from 'dayjs';
import userData from 'tradera/apps/syi/services/user-data';
import draftService from 'tradera/apps/syi/services/draft-service';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

type ItemDurationSlice = {
    isRestartAvailable: boolean;
    durationDays: number;
    customEndTime?: string;
    allowZeroDayAuctions: boolean;
    validationErrors: object;
};

const getEndDate = (durationDays: number, customEndTime?: string) => {
    const endDate = dayjs().startOf('date').add(durationDays, 'day');
    if (!customEndTime) return endDate;

    const [hours, minutes] = customEndTime.split(':').map(Number);
    return endDate.hour(hours).minute(minutes);
};

const getValidationErrors = (
    durationDays: number,
    allowZeroDayAuctions: boolean,
    customEndTime?: string
) => {
    let customEndTimeError = null;
    const endDate = getEndDate(durationDays, customEndTime);

    if (allowZeroDayAuctions) {
        const earliestEndDate = dayjs().add(5, 'minute');

        if (endDate.isBefore(earliestEndDate)) {
            customEndTimeError = 'syi_validation_duration_too_short_zero_day'; //'Din valda sluttid är för tidig. Annonser får avslutas tidigast efter 5 minuter.';
        }
    } else if (customEndTime) {
        const earliestEndDate = dayjs().add(3, 'day');

        if (endDate.isBefore(earliestEndDate, 'minute')) {
            customEndTimeError = 'syi_validation_duration_too_short'; //'Din valda sluttid är för tidig. Annonser får avslutas tidigast efter 72 timmar.';
        }
    }

    return customEndTimeError ? { customEndTime: customEndTimeError } : {};
};

const initialState: ItemDurationSlice = {
    isRestartAvailable: true,
    durationDays: userData?.defaultDrafts?.newItem?.durationDays,
    customEndTime: undefined,
    allowZeroDayAuctions: false,
    validationErrors: {}
};

const newStateWithoutCampaignRelatedProps = (
    state: ItemDurationSlice,
    durationDays: number,
    allowZeroDayAuctions: boolean,
    customEndTime?: string
) => {
    const validationErrors = getValidationErrors(
        durationDays,
        allowZeroDayAuctions,
        customEndTime
    );
    return {
        ...state,
        durationDays,
        customEndTime,
        validationErrors
    };
};

const itemDurationSlice = createSlice({
    name: 'itemDuration',
    initialState,
    reducers: {
        setDurationDays: (
            state,
            action: PayloadAction<{
                durationDays: number;
                isChangeItem?: boolean;
            }>
        ) => {
            const { durationDays, isChangeItem } = action.payload;
            const validationErrors = isChangeItem
                ? {}
                : getValidationErrors(
                      durationDays,
                      state.allowZeroDayAuctions,
                      state.customEndTime
                  );
            return {
                ...state,
                durationDays,
                validationErrors
            };
        },
        setCustomEndTime: (
            state,
            action: PayloadAction<{
                customEndTime: string;
                isChangeItem?: boolean;
            } | null>
        ) => {
            const { customEndTime, isChangeItem } = action.payload || {};

            const validationErrors =
                customEndTime && !isChangeItem
                    ? getValidationErrors(
                          state.durationDays,
                          state.allowZeroDayAuctions,
                          customEndTime
                      )
                    : {};

            return {
                ...state,
                customEndTime,
                validationErrors
            };
        },
        resetDurationAndEndTime: (state) => {
            const durationDays =
                draftService.getDefaultTemplateValue('durationDays');
            const customEndTime =
                draftService.getDefaultTemplateValue('customEndTime');
            const newState = newStateWithoutCampaignRelatedProps(
                state,
                durationDays,
                state.allowZeroDayAuctions,
                customEndTime
            );

            return newState;
        },
        // maybe this can be removed ..
        resetCustomEndTime: (state) => {
            return {
                ...state,
                customEndTime: initialState.customEndTime
            };
        },
        disableZeroDayAuctions: (state) => {
            const durationDays =
                state.durationDays >= 3 ? state.durationDays : 3;

            return {
                ...state,
                allowZeroDayAuctions: false,
                durationDays
            };
        },
        enableZeroDayAuctions: (state) => {
            return {
                ...state,
                allowZeroDayAuctions: true
            };
        },
        enableRestart: (state) => {
            return {
                ...state,
                isRestartAvailable: true
            };
        }
    },
    extraReducers(builder) {
        builder.addCase('DISABLE_RESTART', (state) => {
            return {
                ...state,
                isRestartAvailable: false
            };
        });
    }
});

export default itemDurationSlice.reducer;

export const {
    setDurationDays,
    setCustomEndTime,
    resetDurationAndEndTime,
    resetCustomEndTime,
    disableZeroDayAuctions,
    enableZeroDayAuctions,
    enableRestart
} = itemDurationSlice.actions;
